import { FormEvent, useCallback, useEffect, useState } from 'react';
import { MoreHorizontal, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { apiErrorMessage, apiFetch, v1Path } from '../../lib/api';
import type { ProjectDto, WorkItemSubtypeDefinition } from '../../lib/work-types';
import { useOverlays } from './overlay-context';
import { BrandEmpty, ErrorAlert, LoadingRows, PageTitle, WorkPageError } from './page-states';

export default function ProjectSubtypesSettings({
  projectId,
  canManage,
}: {
  projectId: string;
  canManage: boolean;
}) {
  const { toast, confirm } = useOverlays();
  const [subtypes, setSubtypes] = useState<WorkItemSubtypeDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<WorkItemSubtypeDefinition | null>(null);
  const [name, setName] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const projectPath = v1Path(`/projects/${projectId}`);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const project = await apiFetch<ProjectDto>(projectPath);
      setSubtypes(project.subtypes ?? []);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load task types'));
      setSubtypes([]);
    } finally {
      setLoading(false);
    }
  }, [projectPath]);

  useEffect(() => {
    void load();
  }, [load]);

  function openCreate() {
    setEditing(null);
    setName('');
    setFormError(null);
    setDialogOpen(true);
  }

  function openEdit(subtype: WorkItemSubtypeDefinition) {
    setEditing(subtype);
    setName(subtype.name);
    setFormError(null);
    setDialogOpen(true);
  }

  async function save(next: Array<{ key?: string; name: string }>, success: string) {
    const project = await apiFetch<ProjectDto>(projectPath, {
      method: 'PATCH',
      body: JSON.stringify({ subtypes: next }),
    });
    setSubtypes(project.subtypes ?? []);
    toast(success);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setFormError('Name is required.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      if (editing) {
        await save(
          subtypes.map((subtype) =>
            subtype.key === editing.key ? { ...subtype, name: trimmed } : subtype,
          ),
          'Task type updated',
        );
      } else {
        await save([...subtypes, { name: trimmed }], 'Subtype created');
      }
      setDialogOpen(false);
    } catch (err) {
      setFormError(apiErrorMessage(err, 'Failed to save task type'));
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(subtype: WorkItemSubtypeDefinition) {
    const ok = await confirm({
      title: 'Remove task type',
      description: `Remove ${subtype.name} from this stream? Existing cards keep the value, but it can no longer be selected.`,
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    setError(null);
    try {
      await save(
        subtypes.filter((candidate) => candidate.key !== subtype.key),
        'Task type removed',
      );
    } catch (err) {
      const message = apiErrorMessage(err, 'Failed to remove task type');
      setError(message);
      toast(message);
    }
  }

  return (
    <div className="flex flex-1 flex-col">
      <PageTitle
        title="Task types"
        count={subtypes.length}
        action={
          canManage ? (
            <Button type="button" size="sm" onClick={openCreate}>
              <Plus className="mr-1.5 h-4 w-4" />
              New task type
            </Button>
          ) : null
        }
      />
      <p className="mb-4 text-sm text-muted-foreground">
        Choose the task types, like Bug or New feature, that people can pick for tasks in this stream.
      </p>
      <WorkPageError error={error} projectId={projectId} onJoined={() => void load()} />
      {loading ? (
        <LoadingRows columns={2} />
      ) : subtypes.length === 0 ? (
        <BrandEmpty title="No task types yet." />
      ) : (
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="w-48">Key</TableHead>
              {canManage ? <TableHead className="w-20 text-right">Actions</TableHead> : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {subtypes.map((subtype) => (
              <TableRow key={subtype.key}>
                <TableCell className="text-sm">{subtype.name}</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">
                  {subtype.key}
                </TableCell>
                {canManage ? (
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          aria-label={`Actions for ${subtype.name}`}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => openEdit(subtype)}>Edit</DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onSelect={() => void onDelete(subtype)}
                        >
                          Remove
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit task type' : 'New task type'}</DialogTitle>
            <DialogDescription>
              Names must be unique within the stream.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            {formError ? <ErrorAlert>{formError}</ErrorAlert> : null}
            <div className="grid gap-2">
              <Label htmlFor="subtype-name">Name</Label>
              <Input
                id="subtype-name"
                value={name}
                maxLength={64}
                autoFocus
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : editing ? 'Save task type' : 'Create task type'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
